import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { LogistikOrder, type LogistikOrderShippingDetails } from '@chancenwerk/db/LogistikOrder.js';
import { LogistikShippingRecipient, LogistikShippingStatus } from '@chancenwerk/db/enums/LogistikEnums.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-radiobutton.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { g_getShippingStatusColor, g_getShippingStatusIcon } from '../../../../../src/logistik/client/helper.js';

/**
 * @customElement logistik-dialog-merge-orders
 */
@customElement('logistik-dialog-merge-orders')
export class LogistikDialogMergeOrders extends ChromiumDialog<void> {
  static override get element_name() {
    return 'logistik-dialog-merge-orders';
  }

  @property() orders: LogistikOrder[] = [];

  @state() private _selectedShippingDetail = 0;

  private _newOrderNumber = '';

  override heading = 'Ausgewählte Bestellungen zusammenfassen';

  private get _distinctShippingDetails() {
    const allShippingDetails = this.orders.map(order => order.shippingDetails);
    const schoolShippingDetails = allShippingDetails
      .filter(shippingDetail => shippingDetail.shippingRecipient === LogistikShippingRecipient.SCHOOL)
      .reduce((acc, curr) => {
        if (!acc.find(shippingDetail => shippingDetail.shippingSchoolID === curr.shippingSchoolID)) acc.push(curr);
        return acc;
      }, [] as LogistikOrderShippingDetails[]);

    const schukoShippingDetails = allShippingDetails
      .filter(shippingDetail => shippingDetail.shippingRecipient === LogistikShippingRecipient.SCHUKO)
      .reduce((acc, curr) => {
        if (
          !acc.find(
            shippingDetail =>
              shippingDetail.shippingRecipientName === curr.shippingRecipientName &&
              shippingDetail.shippingAddress.strasse === curr.shippingAddress.strasse &&
              shippingDetail.shippingAddress.hausnummer === curr.shippingAddress.hausnummer &&
              shippingDetail.shippingAddress.zipCity.toString() === shippingDetail.shippingAddress.zipCity.toString(),
          )
        )
          acc.push(curr);
        return acc;
      }, [] as LogistikOrderShippingDetails[]);
    return [...schoolShippingDetails, ...schukoShippingDetails];
  }

  override content() {
    return html`
      <x-flex space="md">
        <cr-input
          placeholder="Neue Auftragsnummer"
          @value-picked=${(e: ValueEvent<string>) => (this._newOrderNumber = e.detail.value)}
        ></cr-input>
        <x-flex horizontal space="sm">
          <x-flex space="md" flex>
            <x-text> Ausgewählte Bestellungen (${this.orders.length}) </x-text>
            <x-flex space="sm">
              ${repeat(
                this.orders,
                order =>
                  html`
                    <cr-basic-card space="sm" padding="sm">
                      <x-flex horizontal space="sm" crossaxis-center>
                        <cr-icon ${css({ fontSize: '1.2rem', color: g_getShippingStatusColor(order.status) })}>
                          ${g_getShippingStatusIcon(order.status)}
                          <cr-tooltip .text=${html`<cr-text>Status: ${order.status}</cr-text>`}></cr-tooltip>
                        </cr-icon>
                        <cr-text bold> ${this._getOrderNumber(order)} </cr-text>
                      </x-flex>
                      <cr-text> ${order.shippingDetails.shippingSchool?.name ?? '-'} </cr-text>
                    </cr-basic-card>
                  `,
              )}
            </x-flex>
          </x-flex>
          <div flex-none ${css({ width: '1px', background: this.colors.neutral.shade(100) })}></div>
          <x-flex space="md" flex>
            <x-text> Mögliche Lieferdetails </x-text>
            <cr-radiogroup
              @value-picked=${(e: ValueEvent<number>) => (this._selectedShippingDetail = e.detail.value)}
              .value=${this._selectedShippingDetail}
            >
              <x-flex
                ${acss({
                  'cr-radiobutton': {
                    padding: `0 ${this.spacing.md}`,
                    '&:nth-child(even)': { background: this.colors.neutral.shade(100) },
                  },
                })}
              >
                ${repeat(
                  this._distinctShippingDetails,
                  (shippingDetails, i) =>
                    html`
                      <cr-radiobutton
                        selectable=${i}
                        .label=${html`
                          <x-flex space="xs" ${css({ padding: `${this.spacing.md} 0` })}>
                            <cr-text>${shippingDetails.shippingRecipient}</cr-text>
                            <cr-text>${shippingDetails.shippingSchool?.name ?? '-'}</cr-text>
                            ${shippingDetails.shippingRecipient === LogistikShippingRecipient.SCHOOL
                              ? html`
                                  <cr-text>${shippingDetails.shippingSchool?.address?.strasse ?? '-'}</cr-text>
                                  <cr-text>${shippingDetails.shippingSchool?.address?.zipCity ?? '-'}</cr-text>
                                `
                              : html`
                                  <cr-text>${shippingDetails.shippingRecipientName ?? 'nicht angegeben'}</cr-text>
                                  <cr-text>
                                    ${shippingDetails.shippingAddress.strasse}
                                    ${shippingDetails.shippingAddress.hausnummer}
                                  </cr-text>
                                  <cr-text>${shippingDetails.shippingAddress.zipCity}</cr-text>
                                `}
                          </x-flex>
                        `}
                      ></cr-radiobutton>
                    `,
                )}
              </x-flex>
            </cr-radiogroup>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  override footerContent() {
    return html`
      <x-flex horizontal end space="sm">
        <cr-button mode="outline" @click=${() => this.close()}> Abbrechen </cr-button>
        <cr-button @click=${async () => this._mergeOrders()}> Bestellungen zusammenfassen </cr-button>
      </x-flex>
    `;
  }

  private _getOrderNumber(order: LogistikOrder) {
    // return entityInstance.orderNumber === '' ? 'nicht vorhanden' : entityInstance.orderNumber;
    if (!order.splitOrderIDs?.length && !order.splitFrom) {
      return order.orderNumber === '' ? 'nicht vorhanden' : order.orderNumber;
    } else {
      if (order.splitOrderIDs?.length) {
        return order.orderNumber + ' #1';
      }
      if (order.splitFrom) {
        return order.orderNumber + ` #${order.splitIndex ? order.splitIndex + 2 : 2}`;
      }
    }
  }

  private async _mergeOrders() {
    if (
      !(await ChromiumDialog.confirm(
        'Möchten Sie diese Bestellungen zusammenfassen? Nur noch die ausgewählten Lieferdetails bleiben dabei erhalten.',
      ))
    )
      return;

    const selectedShippingDetail = this._distinctShippingDetails[this._selectedShippingDetail];
    if (!selectedShippingDetail) return XDialog.alert('Sie müssen eine Versandadresse auswählen!');

    try {
      await ChromiumDialog.waitFor(
        LogistikOrder.mergeOrder(
          new LogistikOrder({
            createdAt: DateTime.fromMillis(Math.max(...this.orders.map(order => order.createdAt.toMillis()))),
            orderNumber: this._newOrderNumber,
            status: LogistikShippingStatus.PROCESSING,
            shippingDetails: {
              ...this._distinctShippingDetails[this._selectedShippingDetail]?.toJSON(),
              dueDate: DateTime.fromMillis(
                Math.max(...this.orders.map(order => order.shippingDetails.dueDate.toMillis())),
              ),
              shippingInstructions: this.orders.map(order => order.shippingDetails.shippingInstructions).join('\n'),
              ...(new Set(this.orders.map(order => order.processorID).filter(Boolean)).size === 1
                ? { processorID: this.orders[0]?.processorID }
                : {}),
            },
            items: this.orders.flatMap(order =>
              order.items.map(item => {
                item._id = A.getGloballyUniqueID();
                return item;
              }),
            ),
            mergeOriginalOrders: this.orders,
          }),
        )(),
      );

      void XSnackbar.show('Bestellungen erfolgreich zusammengefasst');
      await this.close();
    } catch (err) {
      if (err instanceof ValidationError) {
        logger.error({ err }, 'Es gab ein Problem beim Zusammenfassen der Bestellungen');
        void XDialog.alert('Es gab ein Problem beim Zusammenfassen der Bestellungen: ' + err.message);
      } else {
        logger.error({ err }, 'Es gab ein Problem beim Zusammenfassen der Bestellungen');
        void XDialog.alert('Es gab ein Problem beim Zusammenfassen der Bestellungen. Versuchen Sie es später erneut!');
      }
    }
  }
}
